import { useState } from "react";
import {
    Col,
    Container,
    Image,
    Row,
} from "react-bootstrap";

import {
    FaArrowDown,
    FaBrain,
    FaBuilding,
    FaChartLine,
    FaClock,
    FaCode,
    FaDatabase,
    FaGlobe,
    FaHeart,
    FaMapMarkerAlt,
    FaRobot,
    FaSearch,
    FaStar,
} from "react-icons/fa";

const HowPokkoaWorks = () => {
    const [language, setLanguage] = useState("en");

    const isZh = language === "zh";

    const content = {
        en: {
            nav: {
                brand: "Pokkoa",
                how: "How Pokkoa Works",
                language: "中文",
            },

            hero: {
                eyebrow: "POKKOA",
                quote: "Understanding Human Emotion for the AI Era",
                title:
                    "We capture human emotional signals through everyday interactions, transform them into privacy-first aggregated intelligence, and make them accessible through AI-native data services.",
            },

            story: {
                eyebrow: "WHY POKKOA EXISTS",
                title: "Building Pokkoa",
                heading:
                    "Bridging symbolic wisdom, cognitive technology, and human emotion.",
                paragraphs: [
                    "Pokkoa bridges the gap between historical symbolic wisdom and modern cognitive technology, helping people navigate their emotional landscapes with greater clarity.",
                    "Our mission is to create a universal, culturally inclusive framework for meaning-making — empowering people to reflect, understand themselves, and find personal direction through sophisticated AI-driven insights.",
                    "Beyond a digital tool, Pokkoa is evolving into a multidisciplinary creative ecosystem.",
                    "By bringing together original art, narrative storytelling, symbolic wisdom, and digital experiences, we are building a creative universe for the emotionally curious.",
                    "We believe that by connecting self-reflection, mental wellness, technology, and contemporary culture, the journey of understanding ourselves can become not only more insightful, but also more engaging, meaningful, and human.",
                ],
            },

            transition: {
                eyebrow: "FROM SELF-REFLECTION TO EMOTIONAL INTELLIGENCE",
                title: "Every interaction begins with a human moment.",
                description:
                    "A thought. A question. A feeling. A moment of curiosity.",
                body:
                    "Pokkoa turns these moments of self-reflection into structured emotional signals. With privacy at the core, these signals can be anonymized, aggregated, and analyzed across location, time, topics, emotions, and trends.",
                flow:
                    "Human Interaction → Emotional Signals → Emotional Intelligence",
            },

            layer1: {
                number: "01",
                eyebrow: "FRONTEND",
                title: "Capture Human Emotion",
                description:
                    "Pokkoa uses natural and engaging interactive experiences to encourage people to express their thoughts, questions, concerns, and emotions.",
                items: [
                    { icon: FaGlobe, label: "Divination" },
                    { icon: FaStar, label: "Fortune Sticks" },
                    { icon: FaHeart, label: "Tarot" },
                    { icon: FaRobot, label: "AI Conversations" },
                    { icon: FaBrain, label: "Self-reflection" },
                ],
                flow: "Human Interaction → Emotional Input",
            },

            layer2: {
                number: "02",
                eyebrow: "DATA LAYER",
                title: "Understand Emotion at Scale",
                description:
                    "With privacy protection at the core, Pokkoa analyzes anonymized and aggregated emotional data to understand collective emotional patterns.",
                items: [
                    { icon: FaMapMarkerAlt, label: "Location" },
                    { icon: FaClock, label: "Time" },
                    { icon: FaSearch, label: "Topic" },
                    { icon: FaHeart, label: "Emotion" },
                    { icon: FaChartLine, label: "Intensity & Trends" },
                ],
                highlight:
                    "We don't need to know who a person is. We want to understand what people care about, where, when, and how they feel.",
                flow:
                    "Individual Emotional Interactions → Aggregated Emotional Intelligence",
            },

            layer3: {
                number: "03",
                eyebrow: "DATA SERVICE",
                title: "Turn Emotion Into Intelligence",
                description:
                    "Pokkoa delivers privacy-protected, aggregated, and structured emotional intelligence through APIs, MCP, SDKs, and other AI-native data services.",
                technologies: [
                    { icon: FaCode, label: "API" },
                    { icon: FaDatabase, label: "MCP" },
                    { icon: FaCode, label: "SDK" },
                    { icon: FaChartLine, label: "Data Services" },
                ],
                users: [
                    "AI Systems",
                    "AI Agents",
                    "Enterprises",
                    "Finance",
                    "Market Research",
                    "Research",
                ],
                flow: "Emotional Intelligence → Decision Intelligence",
            },

            final: {
                eyebrow: "A NEW LAYER OF HUMAN INTELLIGENCE",
                title: "Emotional intelligence for the AI era.",
                description:
                    "We believe that human emotions often come before logic — shaping what people pay attention to, what they seek, what they fear, and the decisions they ultimately make.",
                body:
                    "By transforming human emotional interactions into privacy-first, aggregated intelligence, Pokkoa aims to make this often-overlooked layer of human experience accessible to the next generation of AI, products, and decision-making systems.",
                statement: "Human emotion comes first.",
                statement2: "We make it understandable.",
            },

            footer: {
                tagline:
                    "AI-Driven Self-Reflection · Emotional Intelligence · Creative Exploration",
            },
        },

        zh: {
            nav: {
                brand: "Pokkoa",
                how: "Pokkoa 如何工作",
                language: "English",
            },

            hero: {
                eyebrow: "POKKOA",
                quote: "理解人类情绪，赋能 AI 时代",
                title:
                    "我们通过真实的人类互动捕捉情绪信号，在保护隐私的前提下，将其转化为聚合式情绪智能，并通过 AI 原生数据服务，让这些情绪信号成为下一代 AI、产品与决策系统的洞察基础。",
            },

            story: {
                eyebrow: "为什么是 POKKOA",
                title: "Building Pokkoa",
                heading: "连接象征智慧、认知科技与人类情绪。",
                paragraphs: [
                    "Pokkoa 连接历史悠久的象征性智慧与现代认知科技，帮助人们更加清晰地理解和探索自己的情绪世界。",
                    "我们的使命，是建立一个具有普适性、跨文化的意义探索框架，让人们通过先进的 AI 驱动洞察进行自我反思、理解自己，并找到属于自己的方向。",
                    "Pokkoa 不只是一个数字工具，也正在逐渐发展成为一个多元的创意生态。",
                    "通过融合原创艺术、叙事故事、象征智慧与数字体验，我们正在构建一个属于「情绪好奇者」的创意宇宙。",
                    "我们相信，当自我反思、心理健康、科技与当代文化连接在一起，自我探索的旅程不仅可以更加深刻，也可以更加有趣、有意义，并更具人性。",
                ],
            },

            transition: {
                eyebrow: "从自我反思到情绪智能",
                title: "每一次互动，都始于一个真实的人类瞬间。",
                description:
                    "一个想法、一个问题、一种情绪，或一个好奇的瞬间。",
                body:
                    "Pokkoa 将这些自我反思的瞬间转化为结构化的情绪信号。在保护隐私的前提下，这些信号可以经过匿名化、聚合，并从地理位置、时间、主题、情绪和趋势等维度进行分析。",
                flow: "人与 AI / 内容互动 → 情绪信号 → 情绪智能",
            },

            layer1: {
                number: "01",
                eyebrow: "前端",
                title: "捕捉人类情绪",
                description:
                    "Pokkoa 通过自然、有趣的互动体验，让用户主动表达当下的想法、问题、关注点和情绪。",
                items: [
                    { icon: FaGlobe, label: "算卦" },
                    { icon: FaStar, label: "抽签" },
                    { icon: FaHeart, label: "塔罗" },
                    { icon: FaRobot, label: "AI 对话" },
                    { icon: FaBrain, label: "自我反思" },
                ],
                flow: "人与 AI / 内容互动 → 情绪输入",
            },

            layer2: {
                number: "02",
                eyebrow: "数据层",
                title: "理解群体情绪",
                description:
                    "在保护用户隐私的前提下，对匿名化、聚合后的情绪数据进行分析，理解不同时间、地点和主题下的群体情绪变化。",
                items: [
                    { icon: FaMapMarkerAlt, label: "地理位置" },
                    { icon: FaClock, label: "时间" },
                    { icon: FaSearch, label: "主题" },
                    { icon: FaHeart, label: "情绪" },
                    { icon: FaChartLine, label: "强度与趋势" },
                ],
                highlight:
                    "我们不需要知道某一个人是谁，而是理解：人们正在关注什么、在哪里、什么时候，以及产生什么情绪。",
                flow: "个体情绪互动 → 聚合式情绪智能",
            },

            layer3: {
                number: "03",
                eyebrow: "数据服务",
                title: "让情绪数据产生价值",
                description:
                    "Pokkoa 将经过隐私保护、聚合和结构化处理后的情绪智能，通过 API、MCP、SDK 以及其他 AI 原生数据服务提供给外部系统。",
                technologies: [
                    { icon: FaCode, label: "API" },
                    { icon: FaDatabase, label: "MCP" },
                    { icon: FaCode, label: "SDK" },
                    { icon: FaChartLine, label: "Data Services" },
                ],
                users: [
                    "AI 系统",
                    "AI Agents",
                    "企业",
                    "金融",
                    "市场研究",
                    "科研",
                ],
                flow: "情绪智能 → 决策智能",
            },

            final: {
                eyebrow: "AI 时代的新一层人类智能",
                title: "为 AI 时代构建情绪智能层。",
                description:
                    "我们相信，人类情绪往往先于逻辑发生——它影响人们关注什么、寻找什么、害怕什么，以及最终做出什么决定。",
                body:
                    "通过将人类情绪互动转化为隐私优先、匿名化和聚合后的情绪智能，Pokkoa 希望让这一层经常被忽略的人类信息，能够服务于下一代 AI、产品与决策系统。",
                statement: "人类情绪先于逻辑。",
                statement2: "我们让它变得可理解。",
            },

            footer: {
                tagline:
                    "AI 驱动的自我反思 · 情绪智能 · 创意探索",
            },
        },
    };

    const t = content[language];

    return (
        <div className="pokkoa-page">

            {/* LANGUAGE SWITCH */}
            <div className="language-switch-container">
                <button
                    type="button"
                    className="language-switch"
                    onClick={() =>
                        setLanguage(isZh ? "en" : "zh")
                    }
                    aria-label={
                        isZh
                            ? "Switch to English"
                            : "切换到中文"
                    }
                >
                    <FaGlobe />
                    <span>
                        {isZh ? "English" : "中文"}
                    </span>
                </button>
            </div>

            {/* HERO */}
            <section className="hero-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={10} className="text-center">
                            <div className="hero-eyebrow">
                                {t.hero.eyebrow}
                            </div>

                            <h1 className="hero-quote">
                                {t.hero.quote}
                            </h1>

                            <p className="hero-title">
                                {t.hero.title}
                            </p>

                            <FaArrowDown className="hero-arrow" />
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* STORY */}
            <section className="story-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={9}>
                            <div className="section-eyebrow">
                                {t.story.eyebrow}
                            </div>

                            <h2>{t.story.title}</h2>

                            <h3>{t.story.heading}</h3>

                            {t.story.paragraphs.map(
                                (paragraph, index) => (
                                    <p key={index}>
                                        {paragraph}
                                    </p>
                                )
                            )}
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* TRANSITION */}
            <section
                id="how-it-works"
                className="transition-section"
            >
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={9} className="text-center">
                            <div className="section-eyebrow">
                                {t.transition.eyebrow}
                            </div>

                            <h2>{t.transition.title}</h2>

                            <p className="transition-description">
                                {t.transition.description}
                            </p>

                            <p>{t.transition.body}</p>

                            <div className="flow-statement">
                                {t.transition.flow}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* LAYER 1 */}
            <section className="layer-section">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={3}>
                            <div className="layer-number">
                                {t.layer1.number}
                            </div>

                            <div className="section-eyebrow">
                                {t.layer1.eyebrow}
                            </div>

                            <h2>{t.layer1.title}</h2>
                        </Col>

                        <Col lg={6}>
                            <p>{t.layer1.description}</p>

                            <div className="icon-grid">
                                {t.layer1.items.map(
                                    (item, index) => {
                                        const Icon = item.icon;

                                        return (
                                            <div
                                                className="icon-item"
                                                key={index}
                                            >
                                                <Icon />
                                                <span>
                                                    {item.label}
                                                </span>
                                            </div>
                                        );
                                    }
                                )}
                            </div>
                        </Col>

                        <Col lg={3}>
                            <div className="flow-card">
                                {t.layer1.flow}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* LAYER 2 */}
            <section className="layer-section layer-section-dark">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={3}>
                            <div className="layer-number">
                                {t.layer2.number}
                            </div>

                            <div className="section-eyebrow">
                                {t.layer2.eyebrow}
                            </div>

                            <h2>{t.layer2.title}</h2>
                        </Col>

                        <Col lg={6}>
                            <p>{t.layer2.description}</p>

                            <div className="icon-grid">
                                {t.layer2.items.map(
                                    (item, index) => {
                                        const Icon = item.icon;

                                        return (
                                            <div
                                                className="icon-item"
                                                key={index}
                                            >
                                                <Icon />
                                                <span>
                                                    {item.label}
                                                </span>
                                            </div>
                                        );
                                    }
                                )}
                            </div>

                            <div className="highlight-box">
                                {t.layer2.highlight}
                            </div>
                        </Col>

                        <Col lg={3}>
                            <div className="flow-card">
                                {t.layer2.flow}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* LAYER 3 */}
            <section className="layer-section">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={3}>
                            <div className="layer-number">
                                {t.layer3.number}
                            </div>

                            <div className="section-eyebrow">
                                {t.layer3.eyebrow}
                            </div>

                            <h2>{t.layer3.title}</h2>
                        </Col>

                        <Col lg={6}>
                            <p>{t.layer3.description}</p>

                            <div className="icon-grid">
                                {t.layer3.technologies.map(
                                    (item, index) => {
                                        const Icon = item.icon;

                                        return (
                                            <div
                                                className="icon-item"
                                                key={index}
                                            >
                                                <Icon />
                                                <span>
                                                    {item.label}
                                                </span>
                                            </div>
                                        );
                                    }
                                )}
                            </div>

                            <div className="users-grid">
                                {t.layer3.users.map(
                                    (user, index) => (
                                        <div
                                            className="user-item"
                                            key={index}
                                        >
                                            <FaBuilding />
                                            <span>{user}</span>
                                        </div>
                                    )
                                )}
                            </div>
                        </Col>

                        <Col lg={3}>
                            <div className="flow-card">
                                {t.layer3.flow}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* FINAL */}
            <section className="final-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={9} className="text-center">
                            <div className="section-eyebrow">
                                {t.final.eyebrow}
                            </div>

                            <h2>{t.final.title}</h2>

                            <p className="final-description">
                                {t.final.description}
                            </p>

                            <p>{t.final.body}</p>

                            <div className="final-statement">
                                <span>
                                    {t.final.statement}
                                </span>

                                <span>
                                    {t.final.statement2}
                                </span>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* FOOTER */}
            <footer className="pokkoa-footer">
                <Container>
                    <div className="footer-content">
                        <div className="footer-brand">
                            <Image
                                src="/logo.jpg"
                                alt="Pokkoa Logo"
                                height="36"
                                rounded
                            />
                        </div>

                        <div className="footer-tagline">
                            {t.footer.tagline}
                        </div>
                    </div>
                </Container>
            </footer>

            <style>{`
                .pokkoa-page {
                    background: #ffffff;
                    color: #111827;
                    min-height: 100vh;
                    position: relative;
                }

                /* LANGUAGE SWITCH */

                .language-switch-container {
                    position: absolute;
                    top: 20px;
                    right: 24px;
                    z-index: 1000;
                }

                .language-switch {
                    border: 1px solid #d1d5db;
                    background: #ffffff;
                    color: #374151;
                    border-radius: 999px;
                    padding: 8px 14px;
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-size: 14px;
                }

                .language-switch:hover {
                    background: #f9fafb;
                    border-color: #9ca3af;
                }

                .language-switch:focus-visible {
                    outline: 2px solid #6366f1;
                    outline-offset: 3px;
                }

                .hero-section {
                    padding: 140px 0 120px;
                    background:
                        radial-gradient(
                            circle at 50% 20%,
                            rgba(99, 102, 241, 0.08),
                            transparent 45%
                        ),
                        #ffffff;
                }

                .hero-eyebrow,
                .section-eyebrow {
                    font-size: 12px;
                    letter-spacing: 0.18em;
                    font-weight: 700;
                    color: #6b7280;
                    margin-bottom: 20px;
                }

                .hero-quote {
                    font-size: clamp(42px, 6vw, 82px);
                    line-height: 1.05;
                    font-weight: 700;
                    letter-spacing: -0.04em;
                    max-width: 1000px;
                    margin: 0 auto 35px;
                }

                .hero-title {
                    max-width: 850px;
                    margin: 0 auto;
                    font-size: 20px;
                    line-height: 1.8;
                    color: #4b5563;
                }

                .hero-arrow {
                    margin-top: 55px;
                    color: #9ca3af;
                    font-size: 22px;
                }

                .story-section {
                    padding: 120px 0;
                    background: #f9fafb;
                }

                .story-section h2,
                .transition-section h2,
                .layer-section h2,
                .final-section h2 {
                    font-size: clamp(36px, 5vw, 64px);
                    line-height: 1.1;
                    letter-spacing: -0.035em;
                    margin-bottom: 30px;
                    font-weight: 700;
                }

                .story-section h3 {
                    font-size: 28px;
                    line-height: 1.4;
                    margin-bottom: 35px;
                    font-weight: 600;
                }

                .story-section p,
                .layer-section p,
                .transition-section p,
                .final-section p {
                    font-size: 18px;
                    line-height: 1.85;
                    color: #4b5563;
                    margin-bottom: 22px;
                }

                .transition-section {
                    padding: 140px 0;
                }

                .transition-description {
                    font-size: 26px !important;
                    color: #111827 !important;
                    font-weight: 500;
                }

                .flow-statement {
                    margin-top: 55px;
                    padding: 25px;
                    border-top: 1px solid #d1d5db;
                    border-bottom: 1px solid #d1d5db;
                    font-size: 20px;
                    font-weight: 600;
                }

                .layer-section {
                    padding: 120px 0;
                    border-top: 1px solid #e5e7eb;
                }

                .layer-section-dark {
                    background: #111827;
                    color: #ffffff;
                }

                .layer-section-dark p {
                    color: #d1d5db;
                }

                .layer-section-dark .section-eyebrow {
                    color: #9ca3af;
                }

                .layer-number {
                    font-size: 72px;
                    line-height: 1;
                    font-weight: 700;
                    letter-spacing: -0.05em;
                    margin-bottom: 25px;
                    color: #9ca3af;
                }

                .icon-grid {
                    display: grid;
                    grid-template-columns: repeat(
                        auto-fit,
                        minmax(130px, 1fr)
                    );
                    gap: 15px;
                    margin-top: 35px;
                }

                .icon-item {
                    padding: 20px 12px;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    text-align: center;
                    gap: 10px;
                    min-height: 105px;
                }

                .layer-section-dark .icon-item {
                    border-color: #374151;
                }

                .icon-item svg {
                    font-size: 22px;
                }

                .icon-item span {
                    font-size: 13px;
                    font-weight: 600;
                }

                .flow-card {
                    padding: 25px;
                    border-radius: 16px;
                    background: #f3f4f6;
                    color: #374151;
                    font-weight: 600;
                    line-height: 1.5;
                    text-align: center;
                }

                .layer-section-dark .flow-card {
                    background: #1f2937;
                    color: #ffffff;
                }

                .highlight-box {
                    margin-top: 35px;
                    padding: 25px;
                    border-left: 3px solid #6366f1;
                    background: #f9fafb;
                    color: #374151;
                    font-size: 17px;
                    line-height: 1.7;
                }

                .users-grid {
                    display: grid;
                    grid-template-columns: repeat(
                        auto-fit,
                        minmax(150px, 1fr)
                    );
                    gap: 12px;
                    margin-top: 35px;
                }

                .user-item {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    padding: 12px 15px;
                    background: #f9fafb;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                }

                .final-section {
                    padding: 160px 0;
                    background:
                        radial-gradient(
                            circle at 50% 50%,
                            rgba(99, 102, 241, 0.1),
                            transparent 45%
                        ),
                        #f9fafb;
                }

                .final-description {
                    font-size: 25px !important;
                    line-height: 1.6 !important;
                    color: #111827 !important;
                    font-weight: 500;
                }

                .final-statement {
                    margin-top: 60px;
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    font-size: clamp(34px, 5vw, 62px);
                    line-height: 1.05;
                    font-weight: 700;
                    letter-spacing: -0.04em;
                }

                .final-statement span:last-child {
                    color: #6366f1;
                }

                .pokkoa-footer {
                    padding: 35px 0;
                    border-top: 1px solid #e5e7eb;
                    background: #ffffff;
                }

                .footer-content {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 25px;
                }

                .footer-tagline {
                    color: #6b7280;
                    font-size: 13px;
                    text-align: right;
                }

                @media (max-width: 768px) {
                    .language-switch-container {
                        top: 15px;
                        right: 15px;
                    }

                    .hero-section {
                        padding: 100px 0 80px;
                    }

                    .story-section,
                    .transition-section,
                    .layer-section {
                        padding: 80px 0;
                    }

                    .final-section {
                        padding: 100px 0;
                    }

                    .hero-title {
                        font-size: 17px;
                    }

                    .footer-content {
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .footer-tagline {
                        text-align: left;
                    }
                }
            `}</style>
        </div>
    );
};

export default HowPokkoaWorks;